import { Box, Divider, styled, Typography } from '@mui/material';
import { history } from 'app';
import Elevio from 'elevio/lib/client';
import Button from 'new/components/Button/Button';
import IconFontAwesome from 'new/components/IconFontAwesome';
import LinkButton from 'new/components/LinkButton';
import useActiveOffice from 'new/hooks/useActiveOffice';
import { useSidebarStore } from 'new/hooks/useSidebarStore';
import { useLogout } from 'new/modules/LoginPage/hooks';
import {
  PMS_SUB_URL,
  PROFILE_INFO_SUB_URL,
  USERS_SUB_URL,
} from 'new/modules/OfficeSettings/routes';
import { USER_INFO_SUB_URL } from 'new/modules/UserSettings/routes';
import findRoute from 'new/routing/routes';
import React from 'react';
import { Link } from 'react-router';
import Cookies from 'universal-cookie';
import { TENANT } from 'new/tenants';
import { TenantStruct } from 'new/types/util';
import { useRouteLeavingGuard } from '../RouteLeavingGuard';
import MembershipAgreement from '../MembershipAgreement';

const cookies = new Cookies();

const StyledBox = styled(Box)(({ theme }) => ({
  '.divider': {
    margin: `${theme.spacing(2)} 0`,
  },

  '.membershipsPlans': {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  '.support-phone': {
    color: theme.palette.grey[600],
    fontWeight: 'bold',
    fontStyle: 'normal',
    textAlign: 'center',
    paddingLeft: 48,
    fontSize: '0.875rem',
  },
}));

const CustomTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.primary.dark,
  marginBottom: 2,

  span: {
    fontWeight: 400,
  },
}));

const CustomSectionContent = styled(Box)(({ theme }) => ({
  p: {
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
    color: theme.palette.grey[800],
  },

  a: {
    margin: `2px 0`,
  },
}));

const CustomLink = styled(LinkButton)(({ theme }) => ({
  display: 'block',
  fontSize: '0.875rem',
  lineHeight: '1.4rem',
  color: theme.palette.grey[800],
  textDecoration: 'none',
  paddingLeft: 12,
  cursor: 'pointer',

  '&:hover': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    backgroundColor: '#fff',
  },

  '&.MuiButton-root': {
    marginTop: -5,
    marginBottom: -6,
    padding: theme.spacing(0, '12px'),
  },
}));

const StyledButton = styled(Button)({
  minWidth: 100,
  width: '100%',
  height: 38,
  '&.MuiButton-root': {
    fontSize: '1rem',
  },
});

const { getPath } = findRoute('OfficeSettings');
const brandingRoute = findRoute('BrandingPage');
const billingRoute = findRoute('BillingPage');
const multiLocationbillingRoute = findRoute('MultiLocationBilling').path;
const multiLocationManageUsersRoute = findRoute('ManageUsers').path;
const multiLocationManagePlansRoute = findRoute('ManagePlans').path;

const Question = () => {
  const { user, activeOfficeId } = useActiveOffice();
  const { viewAsStandard } = useSidebarStore();
  const isMultiLocation =
    user?.data?.otherOffices && user.data.otherOffices.length > 1;

  const signupLink = `/accounts/signup/my-${TENANT.professionalName}/${activeOfficeId}`;
  const businessConnectLink = `https://${
    window.location.hostname
  }/businessconnect/${user?.data.dentistInfo?.officeSlug || ''}`;
  const officeSelectorLink = `https://${window.location.hostname}/signup/${
    user?.data.primarySlug || ''
  }`;

  const firstSection: TenantStruct<React.ReactNode> = {
    dhq: (
      <Box className="section">
        <CustomTitle>LINKS</CustomTitle>
        <CustomSectionContent>
          <CustomLink to={'/terms'} isNewTab>
            Member Terms & Conditions
          </CustomLink>
          {viewAsStandard && (
            <CustomLink to={signupLink} isNewTab>
              Patient Signup Page
            </CustomLink>
          )}
          {isMultiLocation && (
            <CustomLink to={officeSelectorLink} isNewTab>
              Office Selector Page
            </CustomLink>
          )}
          {viewAsStandard && (
            <CustomLink to={businessConnectLink} isNewTab>
              BusinessCONNECT Landing Page
            </CustomLink>
          )}
        </CustomSectionContent>
      </Box>
    ),
    vhq: (
      <Box className="section">
        <CustomTitle>LINKS</CustomTitle>
        <CustomSectionContent>
          <CustomLink to={'/terms'} isNewTab>
            Member Terms & Conditions
          </CustomLink>
          <MembershipAgreement
            label="Member Signature Page"
            CustomButton={CustomLink}
          />
          {viewAsStandard && (
            <CustomLink to={signupLink} isNewTab>
              Patient Signup Page
            </CustomLink>
          )}
          {isMultiLocation && (
            <CustomLink to={officeSelectorLink} isNewTab>
              Office Selector Page
            </CustomLink>
          )}
          {TENANT.constants.files.quickstartGuidePdf && (
            <CustomLink to={TENANT.constants.files.quickstartGuidePdf} isNewTab>
              Quick Reference Guide
            </CustomLink>
          )}
          <CustomLink to={TENANT.constants.files.scriptingPdf} isNewTab>
            Membership Plan Talking Points
          </CustomLink>
        </CustomSectionContent>
      </Box>
    ),
  };

  const secondSection: TenantStruct<React.ReactNode | null> = {
    dhq: (
      <Box className="section">
        <CustomTitle>DOWNLOADS</CustomTitle>
        <CustomSectionContent>
          <CustomLink to={TENANT.constants.files.quickstartGuidePdf} isNewTab>
            Quick-Reference Guide
          </CustomLink>
          <CustomLink
            to="https://dentalhq-uploads.s3.amazonaws.com/Tracking+and+Accounting+2024.pdf"
            isNewTab
          >
            Tracking Treatment and Payment
          </CustomLink>
          <MembershipAgreement
            label="Member Signature Page"
            CustomButton={CustomLink}
          />
          <CustomLink to={TENANT.constants.files.scriptingPdf} isNewTab>
            Membership Plan Talking Points
          </CustomLink>
        </CustomSectionContent>
      </Box>
    ),
    vhq: null,
  };

  return (
    <StyledBox>
      <StyledButton
        autoFocus
        variant="contained"
        endIcon={
          <IconFontAwesome
            name="faArrowRightLong"
            color="primaryContrastText"
          />
        }
        onClick={() => Elevio.openHome()}
        style={{ marginBottom: 11 }}
      >
        GET SUPPORT
      </StyledButton>
      <em className="support-phone">or call {TENANT.supportPhone} </em>
      <Divider className="divider" />
      {firstSection[TENANT.id]}
      {!!secondSection[TENANT.id] && <Divider className="divider" />}
      {secondSection[TENANT.id]}
    </StyledBox>
  );
};

export const handleLogout = (logout: () => void) => {
  const isSuperAdmin = cookies.get('beccascookie');

  if (sessionStorage.getItem('wasDisplayedStripeRestrictionModal')) {
    sessionStorage.setItem('wasDisplayedStripeRestrictionModal', 'false');
  }

  if (isSuperAdmin) {
    localStorage.setItem('auth_token', cookies.get('beccascookie'));
    cookies.remove('associationId', { path: '/' });
    cookies.remove('beccascookie', { path: '/' });
    setTimeout(
      () => (window.location = '/admin/dentists' as unknown as Location),
      250,
    );
  } else {
    logout();
  }
};

const Settings: React.FC<{ open?: boolean }> = () => {
  const { user: data, isMultiLocation, isAdmin, isPatient } = useActiveOffice();
  const user = data?.data;
  const { logout } = useLogout();

  const { viewAsStandard } = useSidebarStore();
  const { changeRoute } = useRouteLeavingGuard();

  return (
    <StyledBox>
      {isAdmin && (
        <>
          {(!isMultiLocation || viewAsStandard) && (
            <>
              <Link
                to={
                  isMultiLocation && !viewAsStandard
                    ? multiLocationManagePlansRoute || ''
                    : (findRoute('MembershipPlansManage').path as string)
                }
                className="membershipsPlans"
              >
                {isMultiLocation && !viewAsStandard
                  ? 'Manage Plans >'
                  : 'Membership Plans >'}
              </Link>
              <Divider className="divider" />
            </>
          )}
          <Box className="section">
            <CustomTitle>
              {isMultiLocation && !viewAsStandard ? 'ORG' : 'OFFICE'}{' '}
              <span>Settings</span>
            </CustomTitle>
            <CustomSectionContent>
              {!(isMultiLocation && !viewAsStandard) && (
                <CustomLink
                  onClick={() =>
                    changeRoute(
                      history.createLocation(getPath?.(PROFILE_INFO_SUB_URL)),
                    )
                  }
                >
                  Profile & Branding
                </CustomLink>
              )}
              <CustomLink
                onClick={() =>
                  changeRoute(
                    history.createLocation(
                      isMultiLocation && !viewAsStandard
                        ? multiLocationManageUsersRoute
                        : getPath?.(USERS_SUB_URL),
                    ),
                  )
                }
              >
                {isMultiLocation && !viewAsStandard ? 'Manage Users' : 'Users'}
              </CustomLink>
              {((isMultiLocation && viewAsStandard) || !isMultiLocation) &&
                user?.pmsData?.pmsSubscriptionState != 'unavailable' &&
                TENANT.features.pms && (
                  <CustomLink
                    onClick={() =>
                      changeRoute(
                        history.createLocation(getPath?.(PMS_SUB_URL)),
                      )
                    }
                  >
                    PMS Integration
                  </CustomLink>
                )}
              {isMultiLocation && (
                <CustomLink to={brandingRoute?.path || ''}>Branding</CustomLink>
              )}
              <CustomLink
                to={
                  isMultiLocation && !viewAsStandard
                    ? multiLocationbillingRoute || ''
                    : billingRoute?.path || ''
                }
              >
                Billing{' '}
                {isMultiLocation && !viewAsStandard ? '' : '& Subscription'}
              </CustomLink>
            </CustomSectionContent>
          </Box>
          <Divider className="divider" />
        </>
      )}
      {!isPatient && (
        <Box className="section">
          <CustomSectionContent>
            <CustomLink
              to={
                findRoute('OfficeReferralPage').getPath?.(
                  user?.paymentUniqueId,
                ) as string
              }
              isNewTab={true}
            >
              Refer an Office
            </CustomLink>
          </CustomSectionContent>
        </Box>
      )}
      {isPatient && (
        <Box className="section">
          <CustomSectionContent>
            <CustomLink
              to={
                findRoute('PatientReferralPage').getPath?.(
                  user?.paymentUniqueId,
                ) as string
              }
              isNewTab={true}
            >
              Refer a Friend
            </CustomLink>
          </CustomSectionContent>
        </Box>
      )}
      <Divider className="divider" />
      <Box className="section">
        <CustomTitle>
          MY <span>Settings</span>
        </CustomTitle>
        <CustomSectionContent>
          <CustomLink
            to={
              isPatient
                ? findRoute('PatientDetailsSettingsPage').path
                : (findRoute('UserSettings').getPath?.(
                    USER_INFO_SUB_URL,
                  ) as string)
            }
          >
            User Info
          </CustomLink>
          <CustomLink onClick={() => handleLogout(logout)} isTextInline>
            Log out
          </CustomLink>
        </CustomSectionContent>
      </Box>
    </StyledBox>
  );
};

export { Question, Settings };
